import { useEffect, useRef, useState } from 'react'
import { useDebounce } from 'hooks/useDebounce'
import { fetchShowEpisodeSearchApi } from 'hooks/shows-master'

interface Category {
    id: number
    name: string
    type: string
    share_url?: string
}

interface Props {
    value?: any
    onSelect: (category: any) => void
    placeholder?: string
    selectedItems?: any
}

export default function SearchableCategoryDropdown({
    value,
    onSelect,
    placeholder = 'Search shows & episodes...',
    selectedItems = [],
}: Props) {
    const [query, setQuery] = useState('')
    const [options, setOptions] = useState<Category[]>([])
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const debouncedQuery = useDebounce(query, 400)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const isSelected = (id: number) => {
        return selectedItems.some((s: any) => s.id === id)
    }

    /* 🔍 Fetch categories */
    useEffect(() => {
        let active = true

        if (!debouncedQuery.trim()) {
            setOptions([])
            return () => {
                active = false
            }
        }

        const load = async () => {
            try {
                setLoading(true)
                const res = await fetchShowEpisodeSearchApi(debouncedQuery)
                if (!active) return
                setOptions(res || [])
            } catch (err) {
                console.error(err)
            } finally {
                if (active) setLoading(false)
            }
        }

        load()

        return () => {
            active = false
        }
    }, [debouncedQuery])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!dropdownRef.current?.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        let active = true

        const handleFocus = () => {
            if (!query.trim()) {
                const load = async () => {
                    try {
                        setLoading(true)
                        const res = await fetchShowEpisodeSearchApi('')
                        if (!active) return
                        setOptions(res || [])
                    } catch (err) {
                        console.error(err)
                    } finally {
                        if (active) setLoading(false)
                    }
                }
                load()
            }
        }

        const inputElement = dropdownRef.current?.querySelector('input')
        inputElement?.addEventListener('focus', handleFocus)

        return () => {
            active = false
            inputElement?.removeEventListener('focus', handleFocus)
        }
    }, [query])

    const handleSelect = (item: Category) => {
        onSelect(item)
        setQuery('')
        setOpen(false)
    }

    return (
        <div ref={dropdownRef} className="relative w-full">
            <input
                value={query}
                placeholder={placeholder}
                onChange={(e) => {
                    setQuery(e.target.value)
                    setOpen(true)
                }}
                onFocus={() => setOpen(true)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm
                text-slate-900 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-blue-500
                dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            />

            {open && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {loading && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            Searching...
                        </div>
                    )}

                    {!loading && options.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            No results found
                        </div>
                    )}

                    {options.map((item) => {
                        const added = isSelected(item.id)

                        return (
                            <div
                                key={item.id}
                                onClick={() => {
                                    if (!added) handleSelect(item)
                                }}
                                className={`px-3 py-2 text-sm flex justify-between items-center
                                   ${added
                                        ? 'cursor-not-allowed text-gray-400 bg-gray-50'
                                        : 'cursor-pointer hover:bg-blue-50'
                                    }`}>
                                <div>
                                    <div>{item.name}</div>
                                    <p className="text-xs text-gray-500">
                                        {item.type}
                                    </p>
                                </div>

                                {added && (
                                    <span className="text-xs font-medium text-green-600">
                                        Added
                                    </span>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
